import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Plus, Search, Edit, Trash2, Award } from "lucide-react";
import {
  loyaltyProgramService,
  LoyaltyProgram,
} from "../../services/loyalty-program.service";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";

export default function LoyaltyProgramsPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    id: string;
    name: string;
  }>({
    isOpen: false,
    id: "",
    name: "",
  });
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["loyalty-programs", { search, page }],
    queryFn: () => loyaltyProgramService.getAll({ search, page, limit: 20 }),
  });

  const deleteMutation = useMutation({
    mutationFn: loyaltyProgramService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["loyalty-programs"] });
    },
  });

  const handleDeleteClick = (id: string, name: string) => {
    setDeleteModal({ isOpen: true, id, name });
  };

  const handleDeleteConfirm = () => {
    deleteMutation.mutate(deleteModal.id);
    setDeleteModal({ isOpen: false, id: "", name: "" });
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Award className="h-6 w-6" />
          Loyalty Programs
        </h1>
        <Link
          to="/loyalty-programs/create"
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add Program
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search programs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Points/Amount
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Amount/Point
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Tiers
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Members
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data?.data.map((program: LoyaltyProgram) => (
                  <tr key={program.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium">{program.name}</div>
                      {program.description && (
                        <div className="text-sm text-gray-500 truncate max-w-xs">
                          {program.description}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {program.pointsPerAmount} pts
                    </td>
                    <td className="px-4 py-3 text-sm">
                      ₹{program.amountPerPoint}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {program._count?.tiers || 0}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {program._count?.customerLoyalty || 0}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded text-xs ${
                          program.isActive
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {program.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <Link
                          to={`/loyalty-programs/${program.id}/edit`}
                          className="p-1 text-blue-600 hover:text-blue-800"
                        >
                          <Edit className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={() =>
                            handleDeleteClick(program.id, program.name)
                          }
                          className="p-1 text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data?.pagination && data.pagination.totalPages > 1 && (
          <div className="p-4 border-t flex justify-between items-center">
            <div className="text-sm text-gray-500">
              Showing {(page - 1) * 20 + 1} to{" "}
              {Math.min(page * 20, data.pagination.total)} of{" "}
              {data.pagination.total}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() =>
                  setPage((p) => Math.min(data.pagination.totalPages, p + 1))
                }
                disabled={page === data.pagination.totalPages}
                className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, id: "", name: "" })}
        onConfirm={handleDeleteConfirm}
        title="Delete Loyalty Program"
        message={`Are you sure you want to delete "${deleteModal.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
